import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ArrowRight, ArrowUpRight, CheckCircle2, FilePenLine, Scale, XCircle } from "lucide-react";
import { useApi, useInvalidateOn, type AiProposal } from "@ncp/api-client";
import { formatDate, formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, InfoHint, InlineAlert, MarkdownBody, Skeleton, SourceText, StatusBadge, cn } from "@ncp/ui";
import { useTracedFigures } from "../figures";
import { FigureValue } from "../FigureCheck";

interface AmendmentChange { field: string; from: string; to: string; rationale?: string | null }
interface AmendmentView {
  caseId: string; caseNumber: string; state: string; status: string; title: string; titleAr?: string | null;
  amendmentSummary: string; amendmentSummaryAr?: string | null; rationale?: string | null; outcome?: string | null;
  changes: AmendmentChange[]; declarationAccepted: boolean;
  checklist: Array<{ key: string; label: string; labelAr?: string | null; mandatory: boolean }>;
  original?: {
    caseId: string; caseNumber: string; title: string; agreementTitle: string; agreementDescription?: string | null; agreementType?: string | null; relevantMarket?: string | null; geographicScope?: string | null;
    combinedMarketShare?: number | null; agreementStart?: string | null; agreementEnd?: string | null; conditions?: string | string[] | null; exemptionValidUntil?: string | null; outcome?: string | null;
  } | null;
}

/**
 * Amendment of a granted exemption (Article 9): each change requested set against the exemption as granted, the applicant's reasons,
 * and the original exemption one click away – what the committee needs to decide whether the exemption still holds as amended.
 */
export function AmendmentTab({ caseId, proposals }: { caseId: string; proposals: AiProposal[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const navigate = useNavigate();
  const view = useQuery({ queryKey: ["amendment", caseId], queryFn: () => api.get<AmendmentView>(`/api/exemptions/amendments/${caseId}`) });
  useInvalidateOn(["case.updated", "case.transitioned", "proposal.updated"], [["amendment", caseId]]);
  // Condition 2.3: each share or amount a change states – the applicant's own, not checked against an independent source.
  const traced = useTracedFigures(caseId);
  if (view.isLoading) return <Skeleton className="h-96" />;
  if (!view.data) return <InlineAlert tone="warning">{t("backoffice:amendment.missing")}</InlineAlert>;
  const v = view.data;
  const ar = locale === "ar";
  const o = v.original;
  const summary = (ar ? v.amendmentSummaryAr : v.amendmentSummary) || v.amendmentSummary;
  const latest = proposals.filter((p) => p.status === "Proposed" || p.status === "Approved").sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]" data-amendment-tab>
      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><FilePenLine className="size-4 text-aegold-700" /> {t("backoffice:amendment.changes")} <Badge size="sm" variant="outline">{formatNumber(v.changes.length, locale)}</Badge><InfoHint>{t("backoffice:amendment.changesHint")}</InfoHint></CardTitle>
            {summary ? <CardDescription><SourceText text={summary} /></CardDescription> : null}
          </CardHeader>
          <CardContent className="space-y-3">
            {v.changes.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:amendment.noChanges")}</p> : v.changes.map((c, i) => (
              <div key={`${c.field}-${i}`} className="rounded-xl border border-sand-200 p-3 text-sm" data-change={c.field}>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t(`backoffice:amendment.fields.${c.field}`, { defaultValue: c.field })}</p>
                <div className="mt-1.5 grid items-start gap-2 md:grid-cols-[1fr_auto_1fr]">
                  <div className="rounded-lg bg-sand-50 p-2"><p className="text-[11px] uppercase tracking-wider text-sand-600">{t("backoffice:amendment.asGranted")}</p><p className="mt-0.5"><SourceText text={c.from || "—"} /></p></div>
                  <ArrowRight className="mt-4 hidden size-4 text-aegold-700 md:block rtl:rotate-180" />
                  <div className="rounded-lg bg-aegold-50/60 p-2"><p className="text-[11px] uppercase tracking-wider text-aegold-800">{t("backoffice:amendment.requested")}</p><p className="mt-0.5 font-medium"><SourceText text={c.to || "—"} /></p></div>
                </div>
                {(traced.data?.figures ?? []).some((x) => x.field === `change:${i}`) ? (
                  <div className="mt-2 flex flex-wrap items-start gap-3 text-xs" data-stated-figures>
                    <span className="pt-0.5 text-muted-foreground">{t("backoffice:amendment.statedFigures")}</span>
                    {(traced.data?.figures ?? []).filter((x) => x.field === `change:${i}`).map((x) => <FigureValue key={x.subjectKey} caseId={caseId} row={x} declarant="applicant" />)}
                  </div>
                ) : null}
                {c.rationale ? <p className="mt-2 text-xs text-muted-foreground"><SourceText text={c.rationale} /></p> : null}
              </div>
            ))}
            {v.rationale ? (
              <div className="rounded-xl bg-sand-50 p-3 text-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:amendment.rationale")}</p>
                <MarkdownBody className="prose-sm mt-1">{v.rationale}</MarkdownBody>
              </div>
            ) : null}
          </CardContent>
        </Card>
        {latest ? (
          <Card className="border-aegold-200">
            <CardHeader className="pb-2"><CardTitle className="flex flex-wrap items-center gap-2 text-base">{ar ? latest.titleAr ?? latest.title : latest.title} <StatusBadge status={latest.status} label={t(`status.${latest.status}`, { defaultValue: latest.status })} /></CardTitle><CardDescription>{ar ? latest.summaryAr ?? latest.summary : latest.summary}</CardDescription></CardHeader>
            <CardContent><MarkdownBody className="prose-sm">{latest.finalContentMarkdown ?? (ar ? latest.contentMarkdownAr ?? latest.contentMarkdown : latest.contentMarkdown)}</MarkdownBody></CardContent>
          </Card>
        ) : null}
      </div>
      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base"><Scale className="size-4 text-aegold-700" /> {t("backoffice:amendment.original")}</CardTitle>
            {o ? <CardDescription>{o.caseNumber} · <SourceText text={o.title} /></CardDescription> : null}
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {!o ? <InlineAlert tone="warning">{t("backoffice:amendment.noOriginal")}</InlineAlert> : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  {o.outcome ? <StatusBadge status={o.outcome} label={t(`decisionOutcomes.${o.outcome}`, { defaultValue: o.outcome })} /> : null}
                  {o.exemptionValidUntil ? <Badge size="sm" variant="outline">{t("backoffice:amendment.validUntil", { date: formatDate(o.exemptionValidUntil, locale) })}</Badge> : null}
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <Fact label={t("backoffice:amendment.agreement")} value={o.agreementTitle} />
                  <Fact label={t("backoffice:amendment.fields.relevantMarket")} value={o.relevantMarket} />
                  <Fact label={t("backoffice:amendment.fields.territory")} value={o.geographicScope} />
                  <Fact label={t("backoffice:amendment.share")} value={o.combinedMarketShare != null ? `${formatNumber(o.combinedMarketShare, locale)}%` : null} />
                  <Fact label={t("backoffice:amendment.term")} value={o.agreementStart || o.agreementEnd ? `${o.agreementStart ? formatDate(o.agreementStart, locale) : "—"} – ${o.agreementEnd ? formatDate(o.agreementEnd, locale) : "—"}` : null} />
                  <Fact label={t("backoffice:amendment.fields.conditions")} value={o.conditions} />
                </dl>
                <button type="button" className="inline-flex items-center gap-1 text-xs text-aegold-800 underline" onClick={() => navigate(`/cases/${o.caseId}`)}><ArrowUpRight className="size-3" /> {t("backoffice:amendment.openOriginal")}</button>
              </>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t("backoffice:amendment.filing")}</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className={cn("flex items-center gap-1.5 text-xs", v.declarationAccepted ? "text-success-700" : "text-warning-700")}>
              {v.declarationAccepted ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />} {v.declarationAccepted ? t("backoffice:amendment.declared") : t("backoffice:amendment.notDeclared")}
            </p>
            <ul className="space-y-1 text-xs">
              {v.checklist.map((i) => <li key={i.key} className="flex items-center gap-2"><Badge size="sm" variant={i.mandatory ? "gold" : "outline"}>{i.mandatory ? t("backoffice:amendment.mandatory") : t("backoffice:amendment.optional")}</Badge><span>{ar ? i.labelAr ?? i.label : i.label}</span></li>)}
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/** A fact of the exemption as granted; a list (the conditions) reads one item per line. */
function Fact({ label, value }: { label: string; value?: string | string[] | null }) {
  const items = (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <>
      <dt className="text-sand-600">{label}</dt>
      <dd>{items.length === 1 ? <SourceText text={items[0]} /> : <ul className="list-disc space-y-0.5 ps-4">{items.map((v, i) => <li key={i}><SourceText text={v} /></li>)}</ul>}</dd>
    </>
  );
}
